import { useEffect } from 'react';
import { getAutoUnitCalculation } from 'utils/accessorials/unitCalculation';

/**
 * Entry-UI auto-calculate-units behavior for a single side ('shipper' |
 * 'carrier'). Derives whether the units field is system-managed (per the
 * accessorial's auto_calculate_units + Applies To config), computes the value
 * from the entered Begin/End dates, and pushes it up via `onUnitsChange`
 * whenever it changes. The pure calculation lives in `getAutoUnitCalculation`;
 * this hook owns only the React glue, so functional consumers don't each
 * reimplement the recalc effect.
 *
 * Returns:
 *   - managed : whether this side's units field is read-only/system-managed,
 *   - pending : managed but no value computable yet (dates not both set),
 *   - units   : the computed value (or null when not yet computable),
 *   - error   : i18n key for an invalid range (End before Begin), else null,
 *   - fields  : which sides Applies To covers,
 *   - active  : whether auto-calculate is configured on at all.
 */
export default function useAutoUnitCalculation({
  accessorial,
  beginDate,
  endDate,
  side,
  currentUnits,
  onUnitsChange,
}) {
  const calc = getAutoUnitCalculation(accessorial, { beginDate, endDate });
  const managed =
    calc.active &&
    (side === 'carrier' ? calc.fields.carrier : calc.fields.shipper);
  const pending = managed && calc.units === null;

  useEffect(() => {
    if (!managed) return;
    const current =
      currentUnits === '' || currentUnits == null ? null : Number(currentUnits);
    // Managed but not yet computable: clear any seeded value so the field shows
    // empty rather than a misleading number until Begin/End dates are entered.
    if (calc.units === null) {
      if (current !== null) onUnitsChange('');
      return;
    }
    if (current !== calc.units) {
      onUnitsChange(calc.units);
    }
  }, [managed, calc.units, currentUnits, onUnitsChange]);

  return {
    managed,
    pending,
    units: calc.units,
    error: calc.error,
    fields: calc.fields,
    active: calc.active,
  };
}
